import { ICapturedEvent, EventCallback } from '../BaseAutoTracker';

/**
 * Configuração para gerenciamento de eventos
 */
export interface IEventManagerConfig {
  maxListeners?: number;
  debounceMs?: number;
  throttleMs?: number;
  retryAttempts?: number;
  retryDelay?: number;
  enableLogging?: boolean;
}

/**
 * Estatísticas de eventos
 */
export interface IEventStats {
  totalEvents: number;
  successfulEvents: number;
  failedEvents: number;
  averageResponseTime: number;
  lastEventTime?: number;
}

/**
 * Gerenciador centralizado de eventos para autoTracks
 */
export class EventManager {
  private config: IEventManagerConfig;
  private stats: IEventStats;
  private eventQueue: ICapturedEvent[] = [];
  private isProcessing: boolean = false;
  private timeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private throttles: Map<string, number> = new Map();
  private elementIds = new WeakMap<Element, number>();
  private nextElementId = 0;

  constructor(config: IEventManagerConfig = {}) {
    this.config = {
      maxListeners: 100,
      debounceMs: 300,
      throttleMs: 1000,
      retryAttempts: 3,
      retryDelay: 1000,
      enableLogging: false,
      ...config,
    };

    this.stats = {
      totalEvents: 0,
      successfulEvents: 0,
      failedEvents: 0,
      averageResponseTime: 0,
    };
  }

  /**
   * Processa um evento com debounce/throttle
   */
  public async processEvent(
    event: ICapturedEvent,
    callback: EventCallback,
    eventKey?: string
  ): Promise<void> {
    const key = eventKey || this.generateEventKey(event);

    // Verifica limite de listeners
    if (this.eventQueue.length >= (this.config.maxListeners || 100)) {
      this.log('Limite de eventos atingido, removendo evento mais antigo');
      this.eventQueue.shift();
    }

    // Debounce
    if (this.config.debounceMs) {
      this.clearTimeout(key);
      const timeout = setTimeout(() => {
        this.executeEvent(event, callback);
      }, this.config.debounceMs);
      this.timeouts.set(key, timeout);
      return;
    }

    // Throttle
    if (this.config.throttleMs) {
      const now = Date.now();
      const lastExecution = this.throttles.get(key) || 0;

      if (now - lastExecution < this.config.throttleMs) {
        this.log(`Evento throttled: ${key}`);
        return;
      }

      this.throttles.set(key, now);
    }

    await this.executeEvent(event, callback);
  }

  /**
   * Executa o evento
   */
  private async executeEvent(
    event: ICapturedEvent,
    callback: EventCallback
  ): Promise<void> {
    const startTime = Date.now();
    this.stats.totalEvents++;

    try {
      this.eventQueue.push(event);

      if (!this.isProcessing) {
        await this.processQueue(callback);
      }

      const responseTime = Date.now() - startTime;
      this.updateAverageResponseTime(responseTime);
      this.stats.successfulEvents++;
      this.stats.lastEventTime = Date.now();

      this.log(`Evento processado com sucesso em ${responseTime}ms`);
    } catch (error) {
      this.stats.failedEvents++;
      this.log(`Erro ao processar evento: ${error}`);

      // Retry logic
      if (this.config.retryAttempts && this.config.retryAttempts > 0) {
        setTimeout(() => {
          this.executeEvent(event, callback);
        }, this.config.retryDelay || 1000);
      }
    }
  }

  /**
   * Processa a fila de eventos
   */
  private async processQueue(callback: EventCallback): Promise<void> {
    this.isProcessing = true;

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift();
      if (event) {
        try {
          await callback(event);
        } catch (error) {
          this.log(`Erro no callback: ${error}`);
        }
      }
    }

    this.isProcessing = false;
  }

  /**
   * Gera chave estável para o evento (selector + selectorType + elemento) para debounce/throttle funcionarem.
   */
  private generateEventKey(event: ICapturedEvent): string {
    const elementId = this.getStableElementId(event.element);
    return `${event.selector}-${event.selectorType}-${elementId}`;
  }

  private getStableElementId(element: Element): number {
    let id = this.elementIds.get(element);
    if (id === undefined) {
      id = ++this.nextElementId;
      this.elementIds.set(element, id);
    }
    return id;
  }

  /**
   * Limpa timeout
   */
  private clearTimeout(key: string): void {
    const timeout = this.timeouts.get(key);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(key);
    }
  }

  /**
   * Atualiza tempo médio de resposta
   */
  private updateAverageResponseTime(responseTime: number): void {
    const total = this.stats.successfulEvents + this.stats.failedEvents;
    this.stats.averageResponseTime =
      (this.stats.averageResponseTime * (total - 1) + responseTime) / total;
  }

  /**
   * Log de eventos
   */
  private log(message: string): void {
    if (this.config.enableLogging) {
      console.log(`[EventManager] ${message}`);
    }
  }

  /**
   * Obtém estatísticas
   */
  public getStats(): IEventStats {
    return { ...this.stats };
  }

  /**
   * Limpa todas as filas e timeouts
   */
  public clear(): void {
    this.eventQueue = [];
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts.clear();
    this.throttles.clear();
    this.isProcessing = false;
  }

  /**
   * Atualiza configuração
   */
  public updateConfig(newConfig: Partial<IEventManagerConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Verifica se está processando
   */
  public isProcessingQueue(): boolean {
    return this.isProcessing;
  }

  /**
   * Obtém tamanho da fila
   */
  public getQueueSize(): number {
    return this.eventQueue.length;
  }

  /**
   * Pausa o processamento
   */
  public pause(): void {
    this.isProcessing = true;
  }

  /**
   * Resume o processamento
   */
  public resume(): void {
    this.isProcessing = false;
  }
}
